// Phase 8 · Director / Analytics Console — read helpers.
//
// One RPC drives the page; we also reuse Phase 7 helpers for the
// risk drill-down lists (clearance summary + ratio compliance).

import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { fetchClearanceSummary, fetchRatioCompliance } from "@/lib/dailyOpsCompliance";

export async function fetchAdminAnalyticsOverview() {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase.rpc("rpc_admin_analytics_overview");
  if (error) {
    if (["42883", "42P01", "42703", "PGRST202"].includes(error.code)) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(
          `[adminAnalytics] rpc_admin_analytics_overview schema drift (${error.code}) — ` +
          "returning null. Apply the latest migration if this is unexpected.",
          error,
        );
      }
      return null;
    }
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[adminAnalytics] rpc_admin_analytics_overview error", error);
    }
    throw error;
  }
  return data || null;
}

/** Pull risk drill-down lists in parallel — small helper for the page. */
export async function fetchAdminAnalyticsRisks() {
  const [clearance, ratios] = await Promise.all([
    fetchClearanceSummary().catch(() => []),
    fetchRatioCompliance().catch(() => []),
  ]);
  return { clearance, ratios };
}

/** Module F · trigger an email digest of the current overview to the
 *  caller's profile email. The backend handles the dispatch. */
export async function emailAdminAnalyticsSummary() {
  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token || "";
  const resp = await fetch(`${API}/api/admin/analytics/email-summary`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    const msg = await resp.text();
    throw new Error(msg || `Email failed (${resp.status})`);
  }
  return resp.json();
}

export function deltaArrow(now, prev) {
  if (now == null || prev == null) return null;
  const diff = now - prev;
  if (diff === 0) return { dir: "flat", text: "no change", magnitude: 0 };
  const pct = prev === 0 ? null : Math.round((diff / prev) * 100);
  return {
    dir: diff > 0 ? "up" : "down",
    magnitude: Math.abs(diff),
    text: pct == null
      ? `${diff > 0 ? "+" : ""}${diff}`
      : `${diff > 0 ? "+" : ""}${diff} (${pct > 0 ? "+" : ""}${pct}%)`,
  };
}

/** Decide the "tone" for a delta — lower is better for some metrics
 *  (incidents) and higher is better for others (attendance). */
export function deltaTone(dir, lowerIsBetter = false) {
  if (!dir || dir === "flat") return "neutral";
  if (lowerIsBetter) return dir === "down" ? "ok" : "warn";
  return dir === "up" ? "ok" : "warn";
}
